import { Dispatch, SetStateAction } from "react"
import { MeetingServiceOrder } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import { SelectedMainUserState, CreateOrderDialogFlow, CreateOrderDialogType } from "../createOrderTypes"
import { OutlookBookingSuccessArgs } from "../../../../modules/order/pages/outlookCreateOrderTypes"
import { getUserDetails, GetUserDetailsResponse } from "../../../../modules/users/userApi"
import { createOrderButtonConfig, CreateOrderType } from "../helpers"
import { ModuleName } from "../../../../modules/users/userTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { OrderFormController } from "./OrderFormController"
import { ConfigQuery } from "../createOrderApi"
import { OrderContextState } from "./helpers"

const transformData = (data: GetUserDetailsResponse) => data.user

type CreateOrderDialogContentProps = Readonly<{
    handleClose: () => void
    user: SelectedMainUserState
    activeOrderTab?: CreateOrderType
    dialogType: CreateOrderDialogType
    handleInvalidUserPermissions: () => void
    flowConfig?: CreateOrderDialogFlow
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderDetails?: MeetingServiceOrder
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
}>

export function CreateOrderDialogContent({
    handleClose,
    user,
    activeOrderTab = CreateOrderType.RESOURCE_BOOKING,
    dialogType,
    handleInvalidUserPermissions,
    flowConfig,
    orderContext,
    setOrderContext,
    orderDetails,
    handleOutlookBookingCompleted,
}: CreateOrderDialogContentProps) {
    const isSimpleOrder = dialogType === CreateOrderDialogType.ORDER

    const { data: userDetails } = useApiQuery(getUserDetails, {
        queryName: ConfigQuery.USER_DETAILS,
        dependencies: { userId: user.userId },
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
        enabled: !!user.userId,
    })

    const baseFilteredTabButtonConfig = (() => {
        if (!userDetails?.modules) return createOrderButtonConfig

        const isCateringActive = userDetails.modules.some((m) => m.name === ModuleName.CATERING && m.isActive)
        const isBookingActive = userDetails.modules.some((m) => m.name === ModuleName.BOOKING && m.isActive)

        return createOrderButtonConfig.map((button) => {
            if (button.value === CreateOrderType.MEETING_SERVICE) {
                const disabled = !isCateringActive
                return { ...button, disabled }
            }
            if (button.value === CreateOrderType.RESOURCE_BOOKING) {
                const disabled = !!flowConfig?.vstoFlowParam || !isBookingActive
                return { ...button, disabled }
            }
            return button
        })
    })()

    const filteredTabButtonConfig = (() => {
        if (isSimpleOrder) return baseFilteredTabButtonConfig
        return baseFilteredTabButtonConfig.filter((b) => b.value === CreateOrderType.MEETING_SERVICE)
    })()

    const initialOrderType: CreateOrderType = (() => {
        if (!isSimpleOrder) {
            return CreateOrderType.MEETING_SERVICE
        }

        const isInitialActive = filteredTabButtonConfig.some((btn) => btn.value === activeOrderTab && !btn.disabled)
        if (isInitialActive) return activeOrderTab

        const firstActive = filteredTabButtonConfig.find((btn) => !btn.disabled)
        return firstActive ? firstActive.value : activeOrderTab
    })()

    const hasActiveTabs = filteredTabButtonConfig.some((btn) => !btn.disabled)

    if (!hasActiveTabs) {
        handleInvalidUserPermissions()
        return <></>
    }

    return (
        <OrderFormController
            user={user}
            flowConfig={flowConfig}
            initialOrderType={initialOrderType}
            filteredTabButtonConfig={filteredTabButtonConfig}
            handleClose={handleClose}
            orderContext={orderContext}
            setOrderContext={setOrderContext}
            orderDetails={orderDetails}
            handleOutlookBookingCompleted={handleOutlookBookingCompleted}
            dialogType={dialogType}
        />
    )
}
